import { minBy, maxBy } from "lodash";
import { mat2d, vec2, glMatrix } from "gl-matrix";

import {
  TransformTypes,
  Positions,
} from "../../constants/TransformerConstants";
import { rotateMatrixAroundPoint, getRadiansFromAngle } from "../math/Angles";

glMatrix.setMatrixArrayType(Array);

const SNAPPING_SENSITIVITY = 5;

const CENTER_HANDLE_QUADRANT_MAP = {
  0: Positions.TOP_MIDDLE,
  1: Positions.RIGHT_MIDDLE,
  2: Positions.BOTTOM_MIDDLE,
  3: Positions.LEFT_MIDDLE,
};

const CENTER_HANDLE_QUADRANT = {
  [Positions.TOP_MIDDLE]: 0,
  [Positions.RIGHT_MIDDLE]: 1,
  [Positions.BOTTOM_MIDDLE]: 2,
  [Positions.LEFT_MIDDLE]: 3,
};

const isCenterHandle = (handle) =>
  Object.keys(CENTER_HANDLE_QUADRANT).includes(handle);

/**
 * @typedef {Object} CenterSnapThreshold
 * @prop {number=} x - threshold value for the x axis
 * @prop {number=} y - threshold value for the x axis
 */

/**
 * Finds the lowest valid snap point's index
 * @param {number} left - lowest index
 * @param {number} right - highest index
 * @param {"x" | "y"} axis
 * @param {import("./GetSelectedCanvasSnapPoints").SnapPoint} snapPoints
 * @param {*} selectionPoint - ??
 * @param {CenterSnapThreshold} centerSnapThreshold - Range from center snap that will still be considered valid
 * @returns {number}
 */
export const findLowerSnapIndex = (
  left,
  right,
  axis,
  snapPoints,
  selectionPoint,
  centerSnapThreshold,
) => {
  while (left < right) {
    const mid = (left + right) >> 1;

    Math.abs(snapPoints[axis][mid][0] - selectionPoint) <
      (centerSnapThreshold[axis] ?? SNAPPING_SENSITIVITY) ||
    snapPoints[axis][mid][0] > selectionPoint
      ? (right = mid)
      : (left = mid + 1);
  }
  return left;
};

/**
 * Finds the highest valid snap point's index
 * @param {number} left - lowest index
 * @param {number} right - highest index
 * @param {"x" | "y"} axis
 * @param {import("./GetSelectedCanvasSnapPoints").SnapPoint} snapPoints
 * @param {*} selectionPoint - ??
 * @param {CenterSnapThreshold} centerSnapThreshold - Range from center snap that will still be considered valid
 * @returns {number}
 */
export const findUpperSnapIndex = (
  left,
  right,
  axis,
  snapPoints,
  selectionPoint,
  centerSnapThreshold,
) => {
  while (left < right) {
    const mid = (left + right) >> 1;

    Math.abs(snapPoints[axis][mid][0] - selectionPoint) <
      (centerSnapThreshold[axis] ?? SNAPPING_SENSITIVITY) ||
    snapPoints[axis][mid][0] < selectionPoint
      ? (left = mid + 1)
      : (right = mid);
  }
  return left;
};

/**
 *
 * @typedef {Array<[number, number, number]>} SelectionPoint
 * selectionPoint - bounding rect corner?
 * selectionGuideMin,
 * selectionGuideMax
 */

/**
 * @typedef {Object} SelectionPoints
 * @prop {Array<SelectionPoint>} x
 * @prop {Array<SelectionPoint>} y
 */

/**
 * @typedef {Object} SnapGuide
 * @prop {number} x1
 * @prop {number} x2
 * @prop {number} y1
 * @prop {number} y2
 * @prop {number|string} stroke
 */

/**
 * @typedef {Object} SnappingGuides
 * @prop {Array<SnapGuide>} x
 * @prop {Array<SnapGuide>} y
 */

/**
 * Generates a set of snap points for each axis
 * @param {Object} props
 * @param {number} props.yOffset
 * @param {number} props.newX
 * @param {number} props.newY
 * @param {import("../Transformer/types").tool} props.tool
 * @param {import("../Transformer/types").TransformTypes} props.type
 * @param {keyof typeof import("../../constants/TransformerConstants").Positions} props.handle
 * @returns {SelectionPoints} SelectionPoints
 */
export const findSelectionPoints = ({
  yOffset,
  newX,
  newY,
  tool,
  type,
  handle,
}) => {
  // Account for padding around stage
  const adjustedOffsetNewX = newX;
  const adjustedOffsetNewY = newY - yOffset;

  let selectionPoints = { x: [], y: [] };

  const selectionCenter = vec2.fromValues(
    adjustedOffsetNewX + tool.w / 2,
    adjustedOffsetNewY + tool.h / 2,
  );

  // Always add the center for move tranform
  if (type === TransformTypes.MOVE) {
    selectionPoints.x.push([...selectionCenter, selectionCenter[1]]);
    selectionPoints.y.push([
      selectionCenter[1],
      selectionCenter[0],
      selectionCenter[0],
    ]);
  }
  let selectionCornerPoints = [];
  const angleQuadrant = Math.floor(
    Math.abs(
      tool.a + 45 < 0 ? 360 + ((tool.a + 45) % 360) : (tool.a + 45) % 360,
    ) / 90,
  );

  selectionCornerPoints.push([adjustedOffsetNewX, adjustedOffsetNewY]); // top-left - 0
  selectionCornerPoints.push([adjustedOffsetNewX + tool.w, adjustedOffsetNewY]); // top-right - 1
  selectionCornerPoints.push([
    adjustedOffsetNewX + tool.w,
    adjustedOffsetNewY + tool.h,
  ]); // bottom-right - 2
  selectionCornerPoints.push([adjustedOffsetNewX, adjustedOffsetNewY + tool.h]); // bottom-left - 3

  // TODO: replace indices with constants for readability
  if (type === TransformTypes.RESIZE) {
    switch (handle) {
      case Positions.TOP_MIDDLE:
        selectionCornerPoints = [selectionCornerPoints[0]];
        break;
      case Positions.RIGHT_MIDDLE:
        selectionCornerPoints = [selectionCornerPoints[1]];
        break;
      case Positions.BOTTOM_MIDDLE:
        selectionCornerPoints = [selectionCornerPoints[2]];
        break;
      case Positions.LEFT_MIDDLE:
        selectionCornerPoints = [selectionCornerPoints[3]];
        break;
      case Positions.TOP_LEFT:
        selectionCornerPoints = [selectionCornerPoints[0]];
        break;
      case Positions.TOP_RIGHT:
        selectionCornerPoints = [selectionCornerPoints[1]];
        break;
      case Positions.BOTTOM_RIGHT:
        selectionCornerPoints = [selectionCornerPoints[2]];
        break;
      case Positions.BOTTOM_LEFT:
        selectionCornerPoints = [selectionCornerPoints[3]];
        break;
      default:
    }
  }
  const selectionHasRotation = tool.a !== undefined && tool.a !== 0;

  if (!selectionHasRotation && type === TransformTypes.MOVE) {
    selectionPoints.y.push([
      selectionCornerPoints[0][1],
      selectionCornerPoints[0][0],
      selectionCornerPoints[1][0],
    ]);
    selectionPoints.y.push([
      selectionCornerPoints[3][1],
      selectionCornerPoints[3][0],
      selectionCornerPoints[2][0],
    ]);
    selectionPoints.x.push([
      selectionCornerPoints[0][0],
      selectionCornerPoints[0][1],
      selectionCornerPoints[3][1],
    ]);
    selectionPoints.x.push([
      selectionCornerPoints[1][0],
      selectionCornerPoints[1][1],
      selectionCornerPoints[2][1],
    ]);
  } else {
    const rotateMatrix = mat2d.create();
    mat2d.translate(rotateMatrix, rotateMatrix, selectionCenter);
    mat2d.rotate(rotateMatrix, rotateMatrix, getRadiansFromAngle(tool.a));
    mat2d.translate(
      rotateMatrix,
      rotateMatrix,
      vec2.negate(vec2.create(), selectionCenter),
    );

    const rotatedPoints = selectionCornerPoints.map((point) =>
      vec2.transformMat2d(vec2.create(), point, rotateMatrix),
    );

    // Only use the furthest point(s) on each axis
    const minX = minBy(rotatedPoints, (vec) => vec[0]);
    const maxX = maxBy(rotatedPoints, (vec) => vec[0]);
    const minY = minBy(rotatedPoints, (vec) => vec[1]);
    const maxY = maxBy(rotatedPoints, (vec) => vec[1]);
    if (type === TransformTypes.RESIZE && isCenterHandle(handle)) {
      switch (
        CENTER_HANDLE_QUADRANT_MAP[
          (angleQuadrant + CENTER_HANDLE_QUADRANT[handle]) % 4
        ]
      ) {
        case Positions.TOP_MIDDLE:
          selectionPoints.y.push([minY[1], minY[0], minY[0]]);
          break;
        case Positions.RIGHT_MIDDLE:
          selectionPoints.x.push([maxX[0], maxX[1], maxX[1]]);
          break;
        case Positions.BOTTOM_MIDDLE:
          selectionPoints.y.push([maxY[1], maxY[0], maxY[0]]);
          break;
        case Positions.LEFT_MIDDLE:
          selectionPoints.x.push([minX[0], minX[1], minX[1]]);
          break;
        default:
      }
    } else if (type === TransformTypes.RESIZE) {
      selectionPoints.x.push([minX[0], minX[1], minX[1]]);
      selectionPoints.y.push([minY[1], minY[0], minY[0]]);
    } else {
      selectionPoints.x.push([minX[0], minX[1], minX[1]]);
      selectionPoints.x.push([maxX[0], maxX[1], maxX[1]]);
      selectionPoints.y.push([minY[1], minY[0], minY[0]]);
      selectionPoints.y.push([maxY[1], maxY[0], maxY[0]]);
    }
  }

  return selectionPoints;
};

/**
 * Generates snap guides, their svg x/y attributes, and the x/y delta from valid snaps, and then calls adjust tool to constrain tool movement using that delta.
 * Returns the value from adjustTool.
 * @param {Object} props
 * @param {Object} props.snapPoints
 * @param {number} props.newX
 * @param {number} props.newY
 * @param {number} props.yOffset
 * @param {(props: { snapGuides: SnappingGuides, reset: boolean }) => void} props.setSnappingGuides
 * @param {import("../Transformer/types").tool} props.tool
 * @param {import("../Transformer/types").TransformTypes} props.type
 * @param {keyof typeof import("../../constants/TransformerConstants").Positions} props.handle
 * @param {SelectionPoints} selectionPoints
 * @returns {[number, number]}
 */
export const findSnappedPoints = (
  { snapPoints, newX, newY, yOffset, setSnappingGuides, tool, type, handle },
  selectionPoints,
) => {
  let centerSnapThreshold = {};
  const snapped = {};
  const snappedDelta = {};
  /** @type {SnappingGuides} */
  const snappingGuides = { x: [], y: [] };
  ["x", "y"].forEach((axis) => {
    selectionPoints[axis].forEach(
      ([selectionPoint, selectionGuideMin, selectionGuideMax]) => {
        const lowerSnapIndex = findLowerSnapIndex(
          0,
          snapPoints[axis].length,
          axis,
          snapPoints,
          selectionPoint,
          centerSnapThreshold,
        );
        const upperSnapIndex = findUpperSnapIndex(
          lowerSnapIndex,
          snapPoints[axis].length,
          axis,
          snapPoints,
          selectionPoint,
          centerSnapThreshold,
        );
        // potential points that fall within snap range for this selection point
        const potentialPoints = snapPoints[axis].slice(
          lowerSnapIndex,
          upperSnapIndex,
        );

        if (potentialPoints?.length > 0) {
          // find the closest point to the selection point
          const [
            snappablePoint,
            snappableGuideMin,
            snappableGuideMax,
            strokeType,
          ] = potentialPoints.reduce((prev, curr) =>
            Math.abs(curr[0] - selectionPoint) <
            Math.abs(prev[0] - selectionPoint)
              ? curr
              : prev,
          );
          const snapDelta = Math.abs(snappablePoint - selectionPoint);

          if (!snapped[axis] || snapDelta < Math.abs(snappedDelta[axis])) {
            snapped[axis] = [
              snappablePoint,
              snappableGuideMin,
              snappableGuideMax,
            ];
            snappedDelta[axis] = snappablePoint - selectionPoint;
            snappingGuides[axis] = [
              [
                snappablePoint,
                snappableGuideMin,
                snappableGuideMax,
                selectionGuideMin,
                selectionGuideMax,
                strokeType,
              ],
            ];
          } else if (snappablePoint - selectionPoint === snappedDelta[axis]) {
            if (snappablePoint !== snapped[axis][0]) {
              // matches on a different part of the axis, no need to change snap, but add a guide
              snappingGuides[axis].push([
                snappablePoint,
                snappableGuideMin,
                snappableGuideMax,
                selectionGuideMin,
                selectionGuideMax,
                strokeType,
              ]);
            }
            // expand the min and max y for the guide
            snapped[axis][1] =
              snapped[axis][1] <= snappableGuideMin
                ? snapped[axis][1]
                : snappableGuideMin;
            snapped[axis][2] =
              snapped[axis][2] >= snappableGuideMax
                ? snapped[axis][2]
                : snappableGuideMax;
          }
        }
      },
    );
  });

  ["x", "y"].forEach((axis) => {
    if (snapped[axis]) {
      snappingGuides[axis] = snappingGuides[axis].map((guide) => ({
        x1: axis === "x" ? guide[0] : guide[1] < guide[3] ? guide[1] : guide[3],
        x2: axis === "x" ? guide[0] : guide[2] > guide[4] ? guide[2] : guide[4],
        y1:
          (axis === "y"
            ? guide[0]
            : guide[1] < guide[3]
              ? guide[1]
              : guide[3]) + yOffset,
        y2:
          (axis === "y"
            ? guide[0]
            : guide[2] > guide[4]
              ? guide[2]
              : guide[4]) + yOffset,
        stroke: guide[5],
      }));
    }
  });

  setSnappingGuides({ snapGuides: snappingGuides });
  return adjustTool({ newX, newY, tool, type, handle, snappedDelta });
};

/**
 * Returns an adjusted tool xy based on snap deltas.
 @param {Object} props
 * @param {number} props.newX
 * @param {number} props.newY
 * @param {import("../Transformer/types").tool} props.tool
 * @param {import("../Transformer/types").TransformTypes} props.type
 * @param {keyof typeof import("../../constants/TransformerConstants").Positions} props.handle
 * @param {{ x: number, y: number }} props.snappedDelta
 * @returns {[number,number]}
 */
const adjustTool = ({ newX, newY, tool, type, handle, snappedDelta }) => {
  if (type === TransformTypes.RESIZE) {
    if (isCenterHandle(handle)) {
      const snapDirection = snappedDelta.x ? "x" : "y";
      const snapDist = snappedDelta.x || snappedDelta.y;

      const unsnappedOriginMatrix = mat2d.create();
      rotateMatrixAroundPoint(
        unsnappedOriginMatrix,
        tool.x + tool.w / 2,
        tool.y + tool.h / 2,
        getRadiansFromAngle(tool.a),
      );
      const unsnappedRotatedOrigin = vec2.transformMat2d(
        vec2.create(),
        [tool.x, tool.y],
        unsnappedOriginMatrix,
      );

      if (snapDist) {
        const isBottomHandle = handle === Positions.BOTTOM_MIDDLE;
        const isLeftHandle = handle === Positions.LEFT_MIDDLE;
        const isTopHandle = handle === Positions.TOP_MIDDLE;
        const isRightHandle = handle === Positions.RIGHT_MIDDLE;
        const positiveAngle = Math.abs(
          tool.a < 0 ? 360 + (tool.a % 360) : tool.a % 360,
        );

        const transformMap = {
          0: {
            x: isRightHandle ? snapDist : -snapDist,
            y: isTopHandle ? -snapDist : snapDist,
          },
          90: {
            x: isBottomHandle ? -snapDist : snapDist,
            y: isLeftHandle ? -snapDist : snapDist,
          },
          180: {
            x: isRightHandle ? -snapDist : snapDist,
            y: isBottomHandle ? -snapDist : snapDist,
          },
          270: {
            x: isBottomHandle ? snapDist : -snapDist,
            y: isLeftHandle ? snapDist : -snapDist,
          },
        }[positiveAngle];

        tool[isTopHandle || isBottomHandle ? "h" : "w"] +=
          transformMap[snapDirection];

        const snappedOriginMatrix = mat2d.create();
        rotateMatrixAroundPoint(
          snappedOriginMatrix,
          tool.x + tool.w / 2,
          tool.y + tool.h / 2,
          getRadiansFromAngle(tool.a),
        );
        const snappedRotatedOrigin = vec2.transformMat2d(
          vec2.create(),
          [tool.x, tool.y],
          snappedOriginMatrix,
        );
        const rotatedOriginOffset = vec2.subtract(
          vec2.create(),
          unsnappedRotatedOrigin,
          snappedRotatedOrigin,
        );

        const offsetMap = {
          0: {
            x: isLeftHandle
              ? snapDist - rotatedOriginOffset[0]
              : -rotatedOriginOffset[0],
            y: isTopHandle
              ? snapDist - rotatedOriginOffset[1]
              : -rotatedOriginOffset[1],
          },
          90: {
            x:
              isBottomHandle || isRightHandle
                ? rotatedOriginOffset[0]
                : isTopHandle
                  ? -rotatedOriginOffset[0]
                  : snapDist - rotatedOriginOffset[0],
            y: isLeftHandle ? -rotatedOriginOffset[1] : rotatedOriginOffset[1],
          },
          180: {
            x:
              isRightHandle || isBottomHandle
                ? rotatedOriginOffset[0]
                : isTopHandle
                  ? rotatedOriginOffset[0]
                  : snapDist + rotatedOriginOffset[0],
            y: isBottomHandle
              ? rotatedOriginOffset[1]
              : isTopHandle
                ? snapDist + rotatedOriginOffset[1]
                : -rotatedOriginOffset[1],
          },
          270: {
            x:
              isBottomHandle || isRightHandle
                ? rotatedOriginOffset[0]
                : isLeftHandle
                  ? rotatedOriginOffset[0]
                  : -rotatedOriginOffset[0],
            y: isLeftHandle ? -rotatedOriginOffset[1] : rotatedOriginOffset[1],
          },
        }[positiveAngle];

        newX += offsetMap.x;
        newY += offsetMap.y;
      }
      return [newX, newY];
    }
    const unsnappedOriginMatrix = mat2d.create();
    rotateMatrixAroundPoint(
      unsnappedOriginMatrix,
      tool.x + tool.w / 2,
      tool.y + tool.h / 2,
      getRadiansFromAngle(tool.a),
    );
    const unsnappedRotatedOrigin = vec2.transformMat2d(
      vec2.create(),
      [tool.x, tool.y],
      unsnappedOriginMatrix,
    );

    if (snappedDelta.x || snappedDelta.y) {
      const positiveAngle = Math.abs(
        tool.a < 0 ? 360 + (tool.a % 360) : tool.a % 360,
      );
      if (positiveAngle % 180 === 90 && snappedDelta.x && snappedDelta.y) {
        delete snappedDelta.y;
      }

      const isTopLeftX = handle === Positions.TOP_LEFT && snappedDelta.x;
      const isTopRightX = handle === Positions.TOP_RIGHT && snappedDelta.x;
      const isBottomLeftX = handle === Positions.BOTTOM_LEFT && snappedDelta.x;
      const isBottomRightX =
        handle === Positions.BOTTOM_RIGHT && snappedDelta.x;

      const isTopLeftY = handle === Positions.TOP_LEFT && snappedDelta.y;
      const isTopRightY = handle === Positions.TOP_RIGHT && snappedDelta.y;
      const isBottomLeftY = handle === Positions.BOTTOM_LEFT && snappedDelta.y;
      const isBottomRightY =
        handle === Positions.BOTTOM_RIGHT && snappedDelta.y;

      const xSnapDist = snappedDelta.x || 0;
      const ySnapDist = snappedDelta.y || 0;
      const transformMap = {
        0: {
          x: isTopRightX || isBottomRightX ? xSnapDist : -xSnapDist,
          y: isTopLeftY || isTopRightY ? -ySnapDist : ySnapDist,
        },
        90: {
          x: isBottomRightX || isBottomLeftX ? -xSnapDist : xSnapDist,
          y: isTopLeftY || isBottomLeftY ? -ySnapDist : ySnapDist,
        },
        180: {
          x: isBottomRightX || isTopRightX ? -xSnapDist : xSnapDist,
          y: isBottomLeftY || isBottomRightY ? -ySnapDist : ySnapDist,
        },
        270: {
          x: isBottomRightX || isBottomLeftX ? xSnapDist : -xSnapDist,
          y: isTopLeftY || isBottomLeftY ? ySnapDist : -ySnapDist,
        },
      }[positiveAngle];

      if (snappedDelta.x) {
        positiveAngle % 180 === 0
          ? (tool.w += transformMap.x)
          : (tool.h += transformMap.x);
      }
      if (snappedDelta.y) {
        positiveAngle % 180 === 0
          ? (tool.h += transformMap.y)
          : (tool.w += transformMap.y);
      }

      const snappedOriginMatrix = mat2d.create();
      rotateMatrixAroundPoint(
        snappedOriginMatrix,
        tool.x + tool.w / 2,
        tool.y + tool.h / 2,
        getRadiansFromAngle(tool.a),
      );
      const snappedRotatedOrigin = vec2.transformMat2d(
        vec2.create(),
        [tool.x, tool.y],
        snappedOriginMatrix,
      );
      const rotatedOriginOffset = vec2.subtract(
        vec2.create(),
        unsnappedRotatedOrigin,
        snappedRotatedOrigin,
      );

      const offsetMap = {
        0: {
          x:
            isTopLeftX || isBottomLeftX
              ? xSnapDist - rotatedOriginOffset[0]
              : -rotatedOriginOffset[0],
          y:
            isTopLeftY || isTopRightY
              ? ySnapDist - rotatedOriginOffset[1]
              : -rotatedOriginOffset[1],
        },
        90: {
          x:
            isBottomRightX || isTopRightY || isBottomRightY || isBottomLeftX
              ? rotatedOriginOffset[0]
              : isTopRightX
                ? -rotatedOriginOffset[0]
                : ySnapDist - rotatedOriginOffset[0],
          y:
            isTopLeftY || isBottomLeftY
              ? -rotatedOriginOffset[1]
              : rotatedOriginOffset[1],
        },
        180: {
          x:
            isTopRightX || isBottomRightX
              ? rotatedOriginOffset[0]
              : isTopRightX
                ? rotatedOriginOffset[0]
                : xSnapDist + rotatedOriginOffset[0],
          y:
            isBottomLeftY || isBottomRightY
              ? rotatedOriginOffset[1]
              : isTopRightY || isTopLeftY
                ? ySnapDist + rotatedOriginOffset[1]
                : -rotatedOriginOffset[1],
        },
        270: {
          x:
            isBottomRightY ||
            isTopRightY ||
            isBottomLeftY ||
            isBottomRightX ||
            isBottomLeftX ||
            isTopLeftY
              ? rotatedOriginOffset[0]
              : -rotatedOriginOffset[0],
          y:
            isBottomLeftY || isTopLeftY
              ? -rotatedOriginOffset[1]
              : rotatedOriginOffset[1],
        },
      }[positiveAngle];
      newX += offsetMap.x;
      newY += offsetMap.y;
    }
    return [newX, newY];
  }

  // Move Drag
  return [newX + (snappedDelta.x || 0), newY + (snappedDelta.y || 0)];
};

const FindSnapMatches = (config) =>
  findSnappedPoints(config, findSelectionPoints(config));

export default FindSnapMatches;
